import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { updateSlotTemplate } from "@/api/catalog";
import { getCoverageHeatmap } from "@/api/kpi";
import { listOrganizations } from "@/api/organizations";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/ToastProvider";
import { useAuthStore } from "@/store/authStore";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function cellStatus(count: number, target: number): "ko" | "degraded" | "ok" {
  if (count <= 0) return "ko";
  if (count < target) return "degraded";
  return "ok";
}

function cellColor(status: "ko" | "degraded" | "ok"): string {
  return status === "ko" ? "#c62828" : status === "degraded" ? "#ef6c00" : "#2e7d32";
}

export function HeatmapPage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();
  const toast = useToast();
  const canEdit = usePermission("slot_template:write");

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 100 }),
  });

  const [orgId, setOrgId] = useState<string>("");
  useEffect(() => {
    const first = orgs.data?.items[0];
    if (!orgId && first) {
      setOrgId(me?.active_org_id && orgs.data?.items.find((o) => o.id === me.active_org_id) ? me.active_org_id : first.id);
    }
  }, [orgs.data, orgId, me?.active_org_id]);

  const [fromDate, setFromDate] = useState<string>(() => addDays(todayIso(), -30));
  const [toDate, setToDate] = useState<string>(todayIso);
  const [onlyNonCompliant, setOnlyNonCompliant] = useState(false);

  const heatmap = useQuery({
    queryKey: ["coverage-heatmap", orgId, fromDate, toDate],
    queryFn: () => getCoverageHeatmap(orgId, fromDate, toDate),
    enabled: Boolean(orgId) && fromDate <= toDate,
  });

  // Right-click on a slot header → edit its minimum required headcount.
  const [slotMenu, setSlotMenu] = useState<
    { x: number; y: number; slotId: string; label: string } | null
  >(null);
  const [targetDialog, setTargetDialog] = useState<
    { slotId: string; label: string } | null
  >(null);
  const [targetValue, setTargetValue] = useState(1);

  const targetMutation = useMutation({
    mutationFn: (vals: { slotId: string; target: number }) =>
      updateSlotTemplate(vals.slotId, { target_headcount: vals.target }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coverage-heatmap"] });
      setTargetDialog(null);
      toast(t("heatmap.editTargetSaved"), "success");
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      toast(typeof detail === "string" ? detail : (e as Error).message, "error");
    },
  });

  return (
    <Box>
      <PageHeader title={t("heatmap.title")} subtitle={t("heatmap.subtitle")} helpTerm="heatmap" />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>{t("nav.organizations")}</InputLabel>
          <Select
            label={t("nav.organizations")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          type="date"
          size="small"
          label={t("heatmap.from")}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          size="small"
          label={t("heatmap.to")}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={onlyNonCompliant}
              onChange={(e) => setOnlyNonCompliant(e.target.checked)}
            />
          }
          label={t("heatmap.onlyNonCompliant")}
        />
      </Stack>

      {heatmap.isLoading && <CircularProgress />}
      {heatmap.isError && (
        <Alert severity="error">{(heatmap.error as Error).message}</Alert>
      )}

      {heatmap.data && (
        <Paper variant="outlined" sx={{ overflowX: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `120px repeat(${heatmap.data.slot_templates.length}, minmax(72px, 1fr))`,
              minWidth: 120 + heatmap.data.slot_templates.length * 72,
            }}
          >
            <Box sx={{ p: 1, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }} />
            {heatmap.data.slot_templates.map((s) => (
              <Box
                key={s.id}
                onContextMenu={
                  canEdit
                    ? (e) => {
                        e.preventDefault();
                        setSlotMenu({
                          x: e.clientX + 2,
                          y: e.clientY - 6,
                          slotId: s.id,
                          label: s.label,
                        });
                      }
                    : undefined
                }
                title={canEdit ? t("heatmap.editHint") : ""}
                sx={{
                  p: 1,
                  bgcolor: "action.hover",
                  borderBottom: 1,
                  borderLeft: 1,
                  borderColor: "divider",
                  textAlign: "center",
                  cursor: canEdit ? "context-menu" : "default",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {s.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                  {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}
                </Typography>
                {(s.target_headcount ?? 1) > 1 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="info"
                    label={`min ${s.target_headcount}`}
                    sx={{ mt: 0.5, height: 18, fontSize: 10, "& .MuiChip-label": { px: 0.5 } }}
                  />
                )}
              </Box>
            ))}
            {heatmap.data.rows
              .filter((row) => {
                if (!onlyNonCompliant) return true;
                return heatmap.data!.slot_templates.some((s) => {
                  const c = row.counts[s.id] ?? 0;
                  return cellStatus(c, s.target_headcount ?? 1) !== "ok";
                });
              })
              .map((row) => (
                <Box key={row.date} sx={{ display: "contents" }}>
                  <Box
                    sx={{
                      p: 1,
                      borderTop: 1,
                      borderColor: "divider",
                      fontFamily: "monospace",
                      fontSize: 12,
                    }}
                  >
                    {row.date}
                  </Box>
                  {heatmap.data!.slot_templates.map((s) => {
                    const c = row.counts[s.id] ?? 0;
                    const target = s.target_headcount ?? 1;
                    const status = cellStatus(c, target);
                    return (
                      <Tooltip
                        key={s.id}
                        title={t(`heatmap.cellTooltip.${status}`, {
                          date: row.date,
                          slot: s.label,
                          count: c,
                          target,
                        })}
                        placement="top"
                      >
                        <Box
                          sx={{
                            borderTop: 1,
                            borderLeft: 1,
                            borderColor: "divider",
                            bgcolor: cellColor(status),
                            color: "#fff",
                            textAlign: "center",
                            py: 1,
                            fontWeight: 600,
                            fontFamily: "monospace",
                          }}
                        >
                          {c > 0 ? `${c}/${target}` : "—"}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              ))}
          </Box>
        </Paper>
      )}

      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          sx={{ bgcolor: cellColor("ko"), color: "#fff", fontWeight: 700 }}
          label={t("heatmap.legend.uncovered")}
        />
        <Chip
          size="small"
          sx={{ bgcolor: cellColor("degraded"), color: "#fff", fontWeight: 700 }}
          label={t("heatmap.legend.degraded")}
        />
        <Chip
          size="small"
          sx={{ bgcolor: cellColor("ok"), color: "#fff", fontWeight: 700 }}
          label={t("heatmap.legend.covered")}
        />
      </Stack>

      <Menu
        open={slotMenu !== null}
        onClose={() => setSlotMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={slotMenu ? { top: slotMenu.y, left: slotMenu.x } : undefined}
      >
        <MenuItem disabled>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {slotMenu?.label}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!slotMenu) return;
            const s = heatmap.data?.slot_templates.find((x) => x.id === slotMenu.slotId);
            setTargetValue(s?.target_headcount ?? 1);
            setTargetDialog({ slotId: slotMenu.slotId, label: slotMenu.label });
            setSlotMenu(null);
          }}
        >
          {t("heatmap.editTarget")}
        </MenuItem>
      </Menu>

      <Dialog
        open={targetDialog !== null}
        onClose={() => setTargetDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("heatmap.editTargetTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("heatmap.editTargetHint", { slot: targetDialog?.label ?? "" })}
            </Typography>
            <TextField
              type="number"
              label={t("heatmap.targetLabel")}
              value={targetValue}
              onChange={(e) =>
                setTargetValue(Math.max(1, Math.min(100, Number(e.target.value) || 1)))
              }
              inputProps={{ min: 1, max: 100, step: 1 }}
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTargetDialog(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            disabled={targetMutation.isPending}
            onClick={() => {
              if (!targetDialog) return;
              targetMutation.mutate({ slotId: targetDialog.slotId, target: targetValue });
            }}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
